import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { MarketingService } from '../../core/services/marketing.service';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';

function buildYearMonthOptions() {
  // 旧版 getYearMonthOptions() と同じく前月から12ヶ月。marketing-all / marketing-flyer と同一実装
  const opts: Array<{ id: string; name: string }> = [];
  const now = new JstDate();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  for (let i = 0; i < 12; i++) {
    year = month === 1 ? year - 1 : year;
    month = month === 1 ? 12 : month - 1;
    if (year > 2020 || (year === 2020 && month > 10)) {
      opts.push({
        id: `${year}-${String(month).padStart(2, '0')}`,
        name: `${year}年${month}月`,
      });
    }
  }
  return opts;
}

@Component({
    selector: 'app-marketing-promotion',
    imports: [FormsModule, FlashMessageComponent],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-edit"></i> 編集</span>
    </div>
    <div class="card-body">
      <!-- 旧版 marketing-promotion.html:10-12 の card-body 先頭 <flash-message> 相当 -->
      <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
      <form novalidate name="myForm">
      <div class="row">
        <div class="col-md-4">
          <div class="form-group row">
            <div class="col-md-12">
              <label class="col-form-label">対象年月</label>
              <select class="form-control" id="year_pay" name="year_pay"
                [(ngModel)]="selectedYearMonth" (ngModelChange)="load()">
                @for (opt of yearMonthOptions; track opt.id) {
                  <option [ngValue]="opt.id">{{ opt.name }}</option>
                }
              </select>
            </div>
          </div>
        </div>
      </div>
      </form>
      <hr>
    </div>
    <div class="card-body">
      <div class="col-md-12">
        <form novalidate name="promotionForm">
        <div class="row align-items-end">
          <table class="table table-responsive-sm table-borderless table-ptb-0">
            <thead>
              <tr class="">
                <td colspan="5" class="pt-0 pb-0 text-right"><small>単位: 円</small></td>
              </tr>
              <tr class="table-secondary">
                <td class="pt-0 pb-0" width="30%"><small>店舗名</small></td>
                <td class="pt-0 pb-0"><small>チラシ</small></td>
                <td class="pt-0 pb-0"><small>ポスティング・折込</small></td>
                <td class="pt-0 pb-0"><small>ウェブ媒体</small></td>
                <td class="pt-0 pb-0"><small>その他</small></td>
              </tr>
            </thead>
            <tbody>
              @for (item of items; track item.id) {
                <tr class="border-bottom border-light">
                  <td>{{ item.name }}</td>
                  <td>
                    <input type="number" class="form-control" [(ngModel)]="item.flyerAmount" [name]="'flyer_' + item.id" required>
                  </td>
                  <td>
                    <input type="number" class="form-control" [(ngModel)]="item.insertionAmount" [name]="'insertion_' + item.id" required>
                  </td>
                  <td>
                    <input type="number" class="form-control" [(ngModel)]="item.webMediaAmount" [name]="'web_' + item.id" required>
                  </td>
                  <td>
                    <input type="number" class="form-control" [(ngModel)]="item.otherAmount" [name]="'other_' + item.id" required>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        </form>
        <div class="row">
          <div class="col-md-12 text-right">
            <button type="button" (click)="save()" class="btn btn-success" [disabled]="saving || !isValid()">
              <i class="fa s-icon-save"></i>&nbsp;保存
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class MarketingPromotionComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private marketingService = inject(MarketingService);
  private flash = inject(FlashService);

  selectedYearMonth = '';
  items: any[] = [];
  saving = false;
  readonly yearMonthOptions = buildYearMonthOptions();

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('マーケティング', 'fa fa-area-chart');
    if (this.yearMonthOptions.length > 0) {
      this.selectedYearMonth = this.yearMonthOptions[0].id;
      await this.load();
    }
  }

  async load(): Promise<void> {
    if (!this.selectedYearMonth) return;
    try {
      const data = await this.marketingService.getPromotionList(this.selectedYearMonth) as any[];
      this.items = (data ?? []).map((d: any) => {
        const promotion = Array.isArray(d.promotions)
          ? (d.promotions[0] ?? {})
          : (d.promotions ?? {});
        return {
          id: d.id ?? d.studioId,
          name: d.name ?? d.studio?.name ?? d.studioName,
          promotion: { ...promotion },
          flyerAmount: promotion.flyerAmount ?? d.flyerAmount ?? 0,
          insertionAmount: promotion.insertionAmount ?? d.insertionAmount ?? 0,
          webMediaAmount: promotion.webMediaAmount ?? d.webMediaAmount ?? 0,
          otherAmount: promotion.otherAmount ?? d.otherAmount ?? 0,
        };
      });
    } catch { this.items = []; }
  }

  isValid(): boolean {
    return this.items.every(item =>
      ['flyerAmount', 'insertionAmount', 'webMediaAmount', 'otherAmount']
        .every(key => item[key] !== null && item[key] !== undefined && item[key] !== '' && Number.isFinite(Number(item[key])))
    );
  }

  async save(): Promise<void> {
    if (!this.isValid()) return;
    this.saving = true;
    try {
      const data = this.items.map(item => {
        const promotion = {
          ...item.promotion,
          flyerAmount: Number(item.flyerAmount),
          insertionAmount: Number(item.insertionAmount),
          webMediaAmount: Number(item.webMediaAmount),
          otherAmount: Number(item.otherAmount),
        };
        if (!promotion.id) {
          promotion.targetMonth = this.selectedYearMonth;
          promotion.studioId = item.id;
        }
        return promotion;
      });

      const totalAmount = (promotion: any) =>
        promotion.flyerAmount + promotion.insertionAmount +
        promotion.webMediaAmount + promotion.otherAmount;
      data.sort((a, b) => totalAmount(b) - totalAmount(a));

      let previousTotal: number | undefined;
      let rank = 1;
      let tiedCount = 0;
      for (const promotion of data) {
        const total = totalAmount(promotion);
        if (previousTotal === undefined || previousTotal === total) {
          promotion.rank = rank;
          tiedCount++;
        } else {
          rank += tiedCount;
          promotion.rank = rank;
          tiedCount = 1;
        }
        previousTotal = total;
      }

      await this.marketingService.upsertPromotion(data);
      // 旧版 marketing.js:564-568: Flash・先頭へscroll・一覧再取得
      this.flash.create('success', '販促実施状況を更新しました');
      window.scrollTo({ top: 0 });
      await this.load();
    } finally {
      this.saving = false;
    }
  }
}
